import {
  Ban,
  Building2,
  ExternalLink,
  KeyRound,
  Power,
  Trash2,
  User,
} from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import type { OnePasswordItemSchema } from '@/services/api/generated/onePasswordService';
import {
  useOnepasswordDeleteItemMutation,
  useOnepasswordListItemsQuery,
  useOnepasswordUpdateItemMutation,
} from '@/services/api/onePasswordService';

import { AddOnePasswordDialog } from './AddOnePasswordDialog';

interface OnePasswordListProps {
  firmId: string;
  /** Optional user ID to filter credentials for a specific user */
  userId?: string;
  /** If true, shows only items for the specified user. If false, shows all firm items. */
  filterByUser?: boolean;
}

const OnePasswordListSkeleton: React.FC = () => (
  <div className="space-y-4">
    <div className="flex justify-between items-center mb-4">
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-10 w-32" />
    </div>
    <div className="space-y-3">
      {[1, 2].map((i) => (
        <div key={i} className="border rounded-lg p-4 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1">
              <Skeleton className="h-8 w-8 rounded" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
            <Skeleton className="h-8 w-20" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const TYPE_LABELS: Record<string, string> = {
  cms_credentials: 'CMS Credentials',
};

export const OnePasswordList: React.FC<OnePasswordListProps> = ({
  firmId,
  userId,
  filterByUser = false,
}) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const {
    data: items = [],
    isLoading,
    refetch,
  } = useOnepasswordListItemsQuery(
    {
      firmId,
      userId: filterByUser ? userId : undefined,
    },
    { skip: !firmId },
  );

  const [deleteItem, { isLoading: isDeleting }] =
    useOnepasswordDeleteItemMutation();
  const [updateItem] = useOnepasswordUpdateItemMutation();

  const handleToggleDisabled = async (item: OnePasswordItemSchema) => {
    const newDisabled = !item.is_disabled;
    try {
      await updateItem({
        id: item.id,
        pk: item.id,
        patchedUpdateOnePasswordItemSchema: { is_disabled: newDisabled },
      }).unwrap();
      toast.success(
        newDisabled
          ? 'OnePassword item disabled'
          : 'OnePassword item enabled',
      );
      refetch();
    } catch (error) {
      console.error('Error toggling OnePassword item:', error);
      toast.error('Failed to update OnePassword item');
    }
  };

  const handleDelete = async (itemId: string) => {
    try {
      await deleteItem({ id: itemId, pk: itemId }).unwrap();
      toast.success('OnePassword item deleted successfully');
      setDeleteConfirmId(null);
      refetch();
    } catch (error) {
      console.error('Error deleting OnePassword item:', error);
      toast.error('Failed to delete OnePassword item');
    }
  };

  const handleAdded = () => {
    refetch();
  };

  if (isLoading) {
    return <OnePasswordListSkeleton />;
  }

  if (items.length === 0) {
    return (
      <>
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <KeyRound className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">
            No OnePassword items
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by adding your first OnePassword item for this firm.
          </p>
          <div className="mt-6">
            <Button onClick={() => setIsAddDialogOpen(true)}>
              Add OnePassword Item
            </Button>
          </div>
        </div>
        <AddOnePasswordDialog
          isOpen={isAddDialogOpen}
          onClose={() => setIsAddDialogOpen(false)}
          firmId={firmId}
          onAdded={handleAdded}
        />
      </>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">OnePassword Items</h3>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          Add OnePassword Item
        </Button>
      </div>

      <div className="space-y-3">
        {items.map((item: OnePasswordItemSchema) => (
          <div
            key={item.id}
            className={`border rounded-lg p-4 bg-white hover:shadow-md transition-all duration-200 ease-in-out ${item.is_disabled ? 'opacity-60' : ''}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 flex-1">
                <KeyRound className="h-8 w-8 text-gray-400" />

                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {item.title}
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      {TYPE_LABELS[item.type] || item.type}
                    </span>
                    {/* Show firm-level or user-level badge */}
                    {item.user_id ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"
                        title={item.user?.email}
                      >
                        <User className="h-3 w-3" />
                        {item.user?.display_name || 'User'}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        <Building2 className="h-3 w-3" />
                        Firm
                      </span>
                    )}
                    {item.is_disabled && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        <Ban className="h-3 w-3" />
                        Disabled
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Vault: {item.vault.title}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {item.private_url && (
                  <a
                    href={item.private_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-gray-100 rounded inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                    title="Open in OnePassword"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>Open</span>
                  </a>
                )}

                <button
                  type="button"
                  onClick={() => handleToggleDisabled(item)}
                  className="p-2 hover:bg-gray-100 rounded"
                  title={item.is_disabled ? 'Enable' : 'Disable'}
                >
                  <Power
                    className={`h-5 w-5 ${item.is_disabled ? 'text-red-500' : 'text-green-500'}`}
                  />
                </button>

                <button
                  type="button"
                  onClick={() => setDeleteConfirmId(item.id)}
                  className="p-2 hover:bg-gray-100 rounded"
                  title="Delete"
                >
                  <Trash2 className="h-5 w-5 text-red-400" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Dialog */}
      <AddOnePasswordDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        firmId={firmId}
        onAdded={handleAdded}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmId !== null}
        onOpenChange={() => setDeleteConfirmId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete OnePassword Item</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this OnePassword item from the
              firm? This will not delete the item from OnePassword itself.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmId(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deleteConfirmId) {
                  handleDelete(deleteConfirmId);
                }
              }}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
